import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authActions, authSelectors } from '@ott/auth';
import {
  actions as virtualCardActions,
  helpers as virtualCardHelpers,
} from '@ott/shared-virtual-card-logic';

import { unwrapResult } from '@reduxjs/toolkit';

import type { UpgradeParams } from '../../../../UpgradeFormModal';
import { ErrorResponse } from '../../../types';
import { FormValues } from '../helpers/constants';

type Props = {
  isSubmitted: boolean;
  withQuickAuthorization: boolean;
  upgradePayload: UpgradeParams;
  setIsPinSent: (isPinSent: boolean) => void;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  onError: (value: ErrorResponse) => void;
};

const useFormSubmit = (props: Props) => {
  // "any" to fix error with .then
  const dispatch = useDispatch<any>();
  const {
    isSubmitted,
    withQuickAuthorization,
    upgradePayload,
    setIsPinSent,
    onSuccess,
    onError,
    onClose,
  } = props;

  const [isAuthorizingNow, setIsAuthorizingNow] = useState(false);

  const { data: authData, sendPinStatus } = useSelector(authSelectors.authSelector);

  const { auth: isAuthorized } = authData ?? {};
  const phone = authData?.auth ? authData.phone : '';
  const cards = authData?.auth ? authData.cards : [];

  const canAuthorize = withQuickAuthorization && !isAuthorized;
  const hasVirtualCard = virtualCardHelpers.hasVirtualCard(cards);

  useEffect(() => {
    if (!isSubmitted || !isAuthorizingNow) {
      return;
    }

    // Успешно отправлен пин
    if (sendPinStatus?.success) {
      setIsPinSent(true);
    }

    // Успешная авторизация
    // Проверка на наличие виртуальной карты
    // Если уже выпущена - закрываем модалку. Если нет - выпускаем.
    if (isAuthorized && sendPinStatus?.success) {
      if (hasVirtualCard) {
        onClose();
      } else {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        handleReleaseCard({ phone });
      }
    }
  }, [isAuthorized, hasVirtualCard, sendPinStatus?.success, isSubmitted, isAuthorizingNow]);

  useEffect(() => {
    setIsAuthorizingNow(false);
  }, []);

  const handleReleaseCard = async (values: FormValues) => {
    return dispatch(virtualCardActions.releaseCard(values))
      .then(unwrapResult)
      .then(() => dispatch(virtualCardActions.upgradeCard(upgradePayload)))
      .then(onSuccess)
      .catch(onError);
  };

  const handleGetVCPinCode = async (values: FormValues) => {
    dispatch(virtualCardActions.getPinCode(values))
      .then(unwrapResult)
      .then((response: string) => {
        if (response === 'PIN_NOT_NEEDED') {
          handleReleaseCard(values);
          return;
        }
        if (response === 'PIN_SENT') {
          setIsPinSent(true);
        }
      })
      .catch(onError);
  };

  const handleGetAuthPinCode = async (values: FormValues) => {
    setIsAuthorizingNow(true);
    dispatch(authActions.getEnterByPhonePinCode(values));
  };

  const handleAuthAndReleaseCard = async (values: FormValues) => {
    const { phone, pin } = values;
    if (phone && pin) {
      dispatch(authActions.enterByPhone({ phone, pin }));
    }
  };

  const releaseCard = async (values: FormValues) => {
    if (canAuthorize) {
      await handleAuthAndReleaseCard(values);
    } else {
      await handleReleaseCard(values);
    }
  };

  const getPinCode = async (values: FormValues) => {
    if (canAuthorize) {
      await handleGetAuthPinCode(values);
    } else {
      await handleGetVCPinCode(values);
    }
  };

  const resetPinCode = () => {
    if (!canAuthorize) {
      dispatch(virtualCardActions.resetPinCode());
    }
  };

  return {
    getPinCode,
    releaseCard,
    resetPinCode,
  };
};

export default useFormSubmit;
